<template>
  <PageBreadcrumbs :items="breadcrumbs" />
  <ContentCard class="mb-8" :loading="loading || isSubmitting">
    <template #header>
      <ContentCardSubheader @back="onCancel">
        {{ t('Reviews.product.campaign.action.editCampaign') }}
        <template #actions>
          <button class="btn-outline-color-s mr-2" data-testid="reviews-campaign-edit-cancel" @click="onCancel">
            {{ t('Reviews.product.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" data-testid="reviews-campaign-edit-save" :disabled="isSubmitting" @click="onSubmit">
            {{ t('Reviews.product.action.saveChanges') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>

    <div class="py-6">
      <div v-if="loading" class="flex justify-center">
        <CircleSpinner />
      </div>
      <ReviewsProductCampaignEditForm
        v-else-if="formData && campaign"
        ref="form"
        :initial-data="formData"
        :campaign="campaign"
        :campaign-started="campaignStarted"
        :product="product"
        @change="onChange"
      />
    </div>

    <template #footer>
      <div class="flex flex-row items-center justify-end">
        <button class="btn-outline-color-s mr-2" data-testid="reviews-campaign-cancel-foot" @click="onCancel">
          {{ t('Reviews.product.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="reviews-campaign-save-foot" :disabled="isSubmitting" @click="onSubmit">
          {{ t('Reviews.product.action.saveChanges') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>

<script setup lang="ts">
import { CircleSpinner } from '@ramp106/omrjs-core-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate } from '@vuelidate/core'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import PageBreadcrumbs from '@/components/PageBreadcrumbs.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import { useReviewsProductBreadcrumbs } from '@/composables/breadcrumbs/useReviewsProductBreadcrumbs'
import {
  mapReviewsServerErrors,
  type CampaignAttributes,
  type ProductData,
  useReviewsIncentivizationCampaignQuery,
  useUpdateReviewsCampaignMutation,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper'
import { ReviewsRouteName } from '@/router/types'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'

import ReviewsProductCampaignEditForm from './ReviewsProductCampaignEditForm.vue'
import type { CampaignEditFormData } from './types'

const props = defineProps<{
  product: ProductData
  campaignId: string
}>()

const { t } = useI18n()
const router = useRouter()
const confirmCloseModal = useTemplateRef('confirmCloseModal')
const form = useTemplateRef<InstanceType<typeof ReviewsProductCampaignEditForm>>('form')

const { result, loading, refetch } = useReviewsIncentivizationCampaignQuery(
  computed(() => ({ campaignId: props.campaignId })),
  { clientId: 'reviews' },
)

const campaign = computed(() => result.value?.manage?.incentivizationCampaign)

const campaignTitle = computed(() => campaign.value?.title || t('Reviews.product.view.campaignDetailView.loading'))
const breadcrumbs = useReviewsProductBreadcrumbs(props.product, campaignTitle)

const formData = ref<CampaignEditFormData | null>(null)
const isSubmitting = ref(false)

const backTo = computed(() => ({
  name: ReviewsRouteName.ReviewsProductCampaignOverview,
  params: { productId: props.product.id, campaignId: props.campaignId },
}))

const campaignStarted = computed(() => {
  if (!campaign.value?.validFrom) return false
  const today = new Date().toISOString().split('T')[0]
  return campaign.value.validFrom <= today
})

watch(
  campaign,
  (newCampaign) => {
    if (newCampaign) {
      formData.value = {
        title: newCampaign.title || '',
        validFrom: newCampaign.validFrom,
        validTo: newCampaign.validTo,
      }
    }
  },
  { immediate: true },
)

const { mutate: updateCampaign, onError } = useUpdateReviewsCampaignMutation({
  clientId: 'reviews',
})

function onChange(data: CampaignEditFormData) {
  if (formData.value) {
    Object.assign(formData.value, data)
  }
}

const v$ = useVuelidate()

function onCancel() {
  track('campaign_update_cancel', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id, campaign_id: props.campaignId })

  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo.value)
  }
}

async function onSubmit() {
  track('campaign_update', { surface: 'editor', entity_type: 'tool', entity_id: props.product.id, campaign_id: props.campaignId })

  if (isSubmitting.value || !formData.value) return

  form.value?.clearExternalErrors()
  await form.value?.validate()

  if (!form.value?.isValid) {
    showNotification(t('Reviews.product.notifications.invalidData'), 'error')
    return
  }

  isSubmitting.value = true

  const attributes: CampaignAttributes = {
    title: formData.value.title,
    validTo: formData.value.validTo || null,
  }

  if (!campaignStarted.value) {
    attributes.validFrom = formData.value.validFrom
  }

  const response = await updateCampaign({
    campaign: props.campaignId,
    attributes,
  })

  const updatedCampaign = response?.data?.manage.updateCampaign
  if (updatedCampaign?.errors && updatedCampaign.errors.length > 0) {
    form.value?.updateExternalErrors(mapReviewsServerErrors(updatedCampaign.errors))
    isSubmitting.value = false
    return
  }

  await refetch()
  showNotification(t('Reviews.product.notifications.savedSuccessfully', { name: formData.value.title }), 'success')
  router.push(backTo.value)
}

onError((error) => {
  showNotification(error.message, 'error')
  isSubmitting.value = false
})
</script>
